// Hero layout data — see plan.md ("Data shape" + Steps 3-5).
// Each entry is pure data ({ label, defaultProps }), no component attached.
// defaultProps match HERO_SCHEMA's shared field shape (built in Step 5,
// extended in Step 10 with imageSource/image-upload): arrangement,
// alignment, primaryColor, backgroundColor, tagline, headline, description,
// ctaText, imageSource, image, imageStyle.

// Step 3: extracted from ck-client-e4f/components/hero-section.tsx.
// Dropped from the real design: 2 floating decorative badges (no field
// represents them). The 4-item features grid IS represented — see
// `features` below (added Step 11) — since E4F/CK's real design nests it
// inside the hero card itself, unlike MEELZ (see heroLayouts.constants.js's
// MEELZ entry + the new, separate featuresGridLayouts.constants.js).
export const HERO_LAYOUT_E4F_DEFAULT_PROPS = {
    arrangement:          "row",
    alignment:            "left",
    primaryColor:         "#b11212",
    ctaButtonColor:       "#b11212",
    ctaButtonTextColor:   "#ffffff",
    backgroundColor:      "#ffffff",
    textColor:            "#1a1a1a",
    featuresIconColor:    "#b11212",
    tagline:         "Healthy Meal Delivery",
    headline:        "Healthy Food",
    description:     "Calories, Proteins, Carbs and Fats / all counted and cooked by restaurant standards",
    ctaText:         "Order now",
    imageSource:     "library",
    image:           "e4f-fresh-ingredients",
    imageStyle:      "boxed",
    // Step 11: real 4 features from default-hero.tsx (identical text across
    // all 3 real repos, confirmed). Real design uses raw inline SVG paths,
    // no named icon library to copy verbatim — closest available AntD icon
    // picked per item (judgment call, per plan.md Step 11).
    features: [
        { icon: "AppleOutlined",       label: { en: "Fresh ingredients" } },
        { icon: "TruckOutlined",       label: { en: "Every day on time delivery" } },
        { icon: "CalendarOutlined",    label: { en: "Flexible meal plans" } },
        { icon: "DashboardOutlined",   label: { en: "Precise nutrient calculation" } },
    ],
    featuresColumns:      2,
    featuresIconPosition: "left",
};

// Step 4: extracted from ck-client-ck/components/hero-section.tsx.
// Same structural template as E4F's real hero (identical layout, different
// colors/copy) — same features-grid/floating-badges gap applies, dropped
// for the same reason confirmed in Step 3.
export const HERO_LAYOUT_CK_DEFAULT_PROPS = {
    arrangement:          "row",
    alignment:            "left",
    primaryColor:         "#46298A",
    ctaButtonColor:       "#46298A",
    ctaButtonTextColor:   "#ffffff",
    backgroundColor:      "#9D91B9",
    textColor:            "#ffffff",
    featuresIconColor:    "#46298A",
    tagline:         "Healthy Meal Delivery",
    headline:        "Healthy Food",
    description:     "Calories, Proteins, Carbs and Fats / all counted and cooked by restaurant standards",
    ctaText:         "Order now",
    imageSource:     "library",
    image:           "ck-fresh-ingredients",
    imageStyle:      "boxed",
    // Step 11: same real 4 features as E4F (identical text confirmed across
    // ck-client-ck/components/default-hero.tsx too).
    features: [
        { icon: "AppleOutlined",       label: { en: "Fresh ingredients" } },
        { icon: "TruckOutlined",       label: { en: "Every day on time delivery" } },
        { icon: "CalendarOutlined",    label: { en: "Flexible meal plans" } },
        { icon: "DashboardOutlined",   label: { en: "Precise nutrient calculation" } },
    ],
    featuresColumns:      2,
    featuresIconPosition: "left",
};

// Step 4: MEELZ's existing values, extracted from REGISTRY_MEELZ's live
// "hero" entry in ClientBuilderPanel.js (HeroPreviewMeelz) — MEELZ's layout
// already exists in production, this only re-expresses it in HERO_SCHEMA's
// shared shape. hero-bg.svg (background texture) is dropped — HERO_SCHEMA
// has one `image` field, mapped to hero-img.png (the foreground shot),
// consistent with dropping E4F/CK's non-schema decorative elements.
export const HERO_LAYOUT_MEELZ_DEFAULT_PROPS = {
    arrangement:          "row",
    alignment:            "left",
    primaryColor:         "#573121",
    ctaButtonColor:       "#573121",
    ctaButtonTextColor:   "#ffffff",
    backgroundColor:      "#FFFBF4",
    textColor:            "#1a1a1a",
    featuresIconColor:    "#573121",
    tagline:         "Healthy Meal Delivery",
    headline:        "Start Your Healthy Meal Plan Today",
    description:     "Choose from weight loss, training, or balanced plans — and enjoy fresh meals delivered daily.",
    ctaText:         "Order now",
    imageSource:     "library",
    image:           "meelz-product-shot",
    imageStyle:      "flat",
    // Correction: MEELZ's real "features" (Fresh Ingredients / Every Day On
    // Time Delivery / Flexible Meal Plans / Precise Nutrient Calculation)
    // live in a completely separate, always-live component (`<Features />`
    // in page.tsx, rendered directly after <HeroSection/>) — NOT inside the
    // hero card. Moved to the new, MEELZ-only `featuresGrid` registry entry
    // (featuresGridLayouts.constants.js). No features fields here for MEELZ
    // — the admin hero editor hides them for MEELZ specifically (see
    // ClientBuilderPanel.js's brandCode==='MEELZ' schema filter).
};

// Step 5: layouts belong to a component type (hero), not a brand — layout
// IDs are arbitrary strings, not required to match brand names (see plan.md
// "Critical architectural correction"). Pure data, no Preview field per entry —
// every layout shares one structural renderer (StructuralHeroPreview / client
// StructuralHero).
export const HERO_LAYOUTS = {
    meelz: { label: "Layout 1", defaultProps: HERO_LAYOUT_MEELZ_DEFAULT_PROPS },
    e4f:   { label: "Layout 2", defaultProps: HERO_LAYOUT_E4F_DEFAULT_PROPS },
    ck:    { label: "Layout 3", defaultProps: HERO_LAYOUT_CK_DEFAULT_PROPS },
};

// Step 10: `imageSource` selects which of the two the `imagePicker` field
// renders/reads — "library" means `image` holds one of HERO_IMAGE_OPTIONS'
// stable ids (below); "upload" means `image` holds a data: URL captured via
// FileReader.readAsDataURL(), same pattern as the admin logo editor.
export const HERO_IMAGE_SOURCE_OPTIONS = ["library", "upload"];

// Fixed set of stable asset IDs (not raw paths) — the same id is stored in
// `image` regardless of which admin/brand publishes it. Each side (this
// admin dashboard, and each of the 3 client repos) resolves the id to its
// OWN local path, since gwt-admin's preview assets and each brand's live
// site assets live in different codebases/domains. This also covers
// cross-brand picking: an admin may publish another brand's image id, so
// every client repo keeps a local copy of all 3 (see plan.md Step 8).
export const HERO_IMAGE_OPTIONS = [
    { value: "meelz-product-shot",     label: "Image 1" },
    { value: "e4f-fresh-ingredients",  label: "Image 2" },
    { value: "ck-fresh-ingredients",   label: "Image 3" },
];

// gwt-admin's OWN preview-only path lookup (this dashboard's public/hero-images/).
// Client repos do NOT use this map — each has its own equivalent, resolving
// the same ids to paths in ITS OWN /public folder.
export const HERO_IMAGE_ADMIN_PREVIEW_PATHS = {
    "meelz-product-shot":    "/hero-images/meelz-hero-img.png",
    "e4f-fresh-ingredients": "/hero-images/e4f-716A3481-min.jpg",
    "ck-fresh-ingredients":  "/hero-images/ck-716A3481-min.jpg",
};

// Each image asset has ONE correct `imageStyle` — not an independent free
// choice. "meelz-product-shot" is a ~99%-transparent product cutout (no
// background), meant for "flat" (object-contain, no crop, no color
// overlay); "e4f-fresh-ingredients"/"ck-fresh-ingredients" are full-bleed
// lifestyle photography, meant for "boxed" (object-cover + gradient
// overlay). Confirmed live bug: an admin on their OWN hero layout (already
// "boxed" by default) picking "meelz-product-shot" from the image dropdown
// alone — without also remembering to flip Image style to "flat" — force-
// crops/overlays a near-fully-transparent image, producing a visibly
// broken/cluttered hero section. SchemaFieldControl.js's imagePicker branch
// auto-applies this alongside the image itself when the image CHANGES
// (never overriding an admin's own already-different imageStyle choice —
// only used as the value to seed/correct to on an actual image pick).
export const HERO_IMAGE_RECOMMENDED_STYLE = {
    "meelz-product-shot":    "flat",
    "e4f-fresh-ingredients": "boxed",
    "ck-fresh-ingredients":  "boxed",
};

export const HERO_ARRANGEMENT_OPTIONS = [
    { value: "row",    label: "Row (image beside text)" },
    { value: "column", label: "Column (image below text)" },
];

export const HERO_ALIGNMENT_OPTIONS = [
    { value: "left",   label: "Left" },
    { value: "center", label: "Center" },
];

// "boxed" = rounded panel + gradient overlay (E4F/CK's real design).
// "flat" = plain image, no wrapper/overlay (MEELZ's real design).
// Lets StructuralHeroPreview (admin) / StructuralHero (client) stay ONE
// generic renderer per side while still matching each layout's real look —
// see plan.md "layouts are data, not a separate hardcoded component."
export const HERO_IMAGE_STYLE_OPTIONS = [
    { value: "boxed", label: "Boxed (rounded panel + overlay)" },
    { value: "flat",  label: "Flat (plain image)" },
];

// Step 11 — features grid: item shape {icon, label}. `icon` uses the shared
// icon-library picker (type: "icon"); `label` is translatable from the
// start (same category as footer's linkColumns[].links[].label,
// ourAdvantage's cards[].title, faq's items[].question — all translatable
// short display copy, no reason to treat this one differently).
export const HERO_FEATURE_FIELDS = [
    { field: "icon",  label: "Icon", type: "icon" },
    { field: "label", label: "Label", translatable: true, wrapControl: true },
];
export const HERO_FEATURE_NEW_ITEM = { icon: "StarOutlined", label: { en: "New Feature" } };

export const HERO_FEATURES_COLUMNS_OPTIONS = [
    { value: 1, label: "1 column" },
    { value: 2, label: "2 columns" },
];

export const HERO_FEATURES_ICON_POSITION_OPTIONS = [
    { value: "left", label: "Icon left of label" },
    { value: "top",  label: "Icon above label" },
];

// One shared set of structural fields, applied to every layout since every
// layout is the same shape — no more per-brand schema array.
export const HERO_SCHEMA = [
    { key: "arrangement",     label: "Arrangement",      type: "select", options: HERO_ARRANGEMENT_OPTIONS },
    { key: "alignment",       label: "Alignment",        type: "select", options: HERO_ALIGNMENT_OPTIONS },
    { key: "primaryColor",    label: "Primary color",    type: "color" },
    { key: "ctaButtonColor",     label: "CTA button color",      type: "color" },
    { key: "ctaButtonTextColor", label: "CTA button text color", type: "color" },
    { key: "backgroundColor",    label: "Background color",      type: "color" },
    { key: "textColor",          label: "Text color",            type: "color" },
    { key: "featuresIconColor",  label: "Features icon color",   type: "color" },
    { key: "tagline",         label: "Tagline",          type: "text", translatable: true, wrapControl: true },
    { key: "headline",        label: "Headline",         type: "text", translatable: true, wrapControl: true },
    { key: "description",     label: "Description",      type: "text", translatable: true, wrapControl: true },
    { key: "ctaText",         label: "Button text",      type: "text", translatable: true, wrapControl: true },
    { key: "image",           label: "Image",            type: "imagePicker", options: HERO_IMAGE_OPTIONS, previewPaths: HERO_IMAGE_ADMIN_PREVIEW_PATHS, autoStyleKey: "imageStyle", autoStyleMap: HERO_IMAGE_RECOMMENDED_STYLE },
    { key: "imageStyle",      label: "Image style",      type: "select", options: HERO_IMAGE_STYLE_OPTIONS },
    {
        key:            "features",
        label:          "Features",
        type:           "cards",
        fields:         HERO_FEATURE_FIELDS,
        newItemDefault: HERO_FEATURE_NEW_ITEM,
    },
    { key: "featuresColumns",      label: "Feature columns",     type: "select", options: HERO_FEATURES_COLUMNS_OPTIONS },
    { key: "featuresIconPosition", label: "Feature icon position", type: "select", options: HERO_FEATURES_ICON_POSITION_OPTIONS },
];
